import L from "leaflet";
import "../../Leaflet.Editable.js"
import 'leaflet-kml'
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {TileLayer} from "react-leaflet/TileLayer";
import {Pane} from "react-leaflet";
import * as Const from "../../Const";
import {BASE_NAME} from "../../Const";
import {MapContainer} from "react-leaflet/MapContainer";
import {Button, Card, CardBody, CardFooter} from "react-bootstrap";
import UserHeader from "../UserHeader";
import useUserSessionInfo from "../../hooks/useUserSessionInfo";
import {useNavigate} from "react-router-dom";
import RoverFeatureSelector from "./RoverFeatureSelector";
import {
    indexGeoFence,
    indexIgnore,
    indexWorkArea,
    rainRoverPathOptions,
    stepIndexGenerateRows,
    stepIndexLoadKML,
    stepIndexPlaceReferencePath,
    stepIndexPlaceWaterSource, stepIndexPlanRoute,
    stepIndexSelectFeatures
} from "./RainRoverConst";
import RoverPathWizard from "./RoverPathWizard";
import RoverWaterSourcePlacement from "./RoverWaterSourcePlacement";
import RoverPathGeneration from "./RoverPathGeneration";
import {getAverageLatLng, getLatLngBounds} from "../../utils/OwnMapMath";
import RainMap from "./RainMap";
import RoverRoutePlanning from "./RoverRoutePlanning";

function RainRoverMain() {

    const TAG = "RRMain";

    const navigate = useNavigate();
    const onSessionError = useMemo(() => () => navigate(BASE_NAME + "/user/login"), [navigate]);

    const [sessionId, userId, userName, remoteAddr] = useUserSessionInfo(onSessionError);

    const mapRef = useRef(null);

    const [rainMap, setRainMap] = useState(undefined);

    const [currentStep, setCurrentStep] = useState(0);
    const [selectedFileName, setSelectedFileName] = React.useState(null);
    const [fieldPaths, setFieldPaths] = useState([]);
    const [fieldPoints, setFieldPoints] = useState([]);



    function getPaths(kmlDoc) {
        const paths = [];
        let id = 0;

        function extractCoords(multi) {
            multi.querySelectorAll('LineString').forEach(el => {
                const coords = getLatLngs(el);
                let name = el['id'];
                if (!name) {
                    name = el.parentElement.querySelector('name').innerHTML;
                }
                let altitudeEl = el.parentElement.querySelector("Data[name='GisRefAlt']")?.querySelector("value");
                if (altitudeEl) {
                    console.log(TAG, "altitude element", altitudeEl.innerHTML);
                    const alt = parseFloat(altitudeEl.innerHTML);
                    if (alt) {
                        coords.forEach(value => value.alt = alt);
                    }
                }

                if (coords) {
                    paths.push( { id: id++,name: name, coords: coords } );
                }
            });
        }

        extractCoords(kmlDoc);

        kmlDoc.querySelectorAll('MultiLineString').forEach(multi => {
            extractCoords(multi);
        });

        kmlDoc.querySelectorAll('MultiGeometry').forEach(multi => {
            extractCoords(multi);
        });

        return paths;
    }



    function getPoints(kmlDoc) {
        const points = [];
        let id = 0;
        kmlDoc.querySelectorAll('Point').forEach(el => {
            const coords = getLatLngs(el);
            if (coords) {
                points.push( { id: id++, coords: coords[0] } );
            }
        });
        return points;
    }



    function getLatLngs(xml) {
        let el = xml.getElementsByTagName('coordinates');
        let coords = [];
        for (let j = 0; j < el.length; j++) {
            coords = coords.concat(readCoords(el[j]));
        }
        return coords;
    }

    function readCoords(el) {
        let text = '', coords = [], i;
        for (i = 0; i < el.childNodes.length; i++) {
            text = text + el.childNodes[i].nodeValue;
        }
        text = text.split(/[\s\n]+/);
        for (i = 0; i < text.length; i++) {
            let ll = text[i].split(',');
            if (ll.length < 3) {
                continue;
            }
            coords.push(new L.LatLng(ll[1], ll[0], ll[2]));
        }
        return coords;
    }






    const handleInputFileChange = (event) => {

        setRainMap(new RainMap(mapRef.current));

        const file = event.target.files[0];

        if (file) {
            setSelectedFileName(file.name);
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target.result;
                const parser = new DOMParser();
                const kml = parser.parseFromString(text, "text/xml");

                console.log(TAG, "kml", kml);
                const paths = getPaths(kml);
                // console.log(TAG, "paths", paths);

                const points = getPoints(kml);
                // console.log(TAG, "points", points);

                let isWorkAreaFound = false;
                let isGeoFenceFound = false;
                let bounds = undefined;

                for (let i = 0; i < paths.length; ++i) {
                    let role = indexIgnore;

                    if (paths[i].name === "HeadlandExteriorPolygon" && !isGeoFenceFound) {
                        isGeoFenceFound = true;
                        role = indexGeoFence;
                        bounds = getLatLngBounds(paths[i].coords);
                        // console.log(TAG, "north", bounds.getNorth());
                    } else if (paths[i].name === "HeadlandInteriorPolygon" && !isWorkAreaFound) {
                        isWorkAreaFound = true;
                        role = indexWorkArea;
                    }

                    let options = rainRoverPathOptions[role];

                    paths[i].role = role;
                    if (options) {
                        const constructionLine = L.polygon(paths[i].coords, options);
                        paths[i].line = constructionLine.addTo(mapRef.current);
                    }
                }

                setFieldPaths(paths);
                setFieldPoints(points);
                setCurrentStep(stepIndexSelectFeatures);

                if (bounds) {
                    // console.log(TAG, "bounds", bounds);
                    mapRef.current.flyToBounds(bounds, { padding: [60, 60], duration: 1 });
                }

            };
            reader.readAsText(file);
        }
    };


    const onFeaturesSelected = useCallback(() => {
        let hasGeoFence = rainMap.hasGeoFence();
        let hasWorkArea = rainMap.hasWorkArea();
        let hasWaterSource = rainMap.hasWaterSourcePos();
        let hasReferencePath = rainMap.hasReferencePath();

        console.log(TAG, hasGeoFence, hasWorkArea, hasWaterSource, hasReferencePath);
        if (hasGeoFence && hasWorkArea) {
            if (hasWaterSource) {
                console.log(TAG, "water source", hasWaterSource);
            }

            if (!hasWaterSource) {
                const average = getAverageLatLng(rainMap.getWorkAreaLatLng());
                console.log(TAG, "water source from average", average);
                rainMap.setWaterSourcePos(average);
                setCurrentStep(stepIndexPlaceWaterSource);
            } else if (!hasReferencePath) {
                setCurrentStep(stepIndexPlaceReferencePath);
            } else {
                setCurrentStep(stepIndexGenerateRows);
            }
        }
    }, [rainMap]);
    
    
    const onWaterSourcePositionDefined = useCallback((positionLatLng) => {
        console.log(TAG, "water source pos", positionLatLng);
        rainMap.setWaterSourcePos(positionLatLng);
        if (rainMap.hasReferencePath()) {
            setCurrentStep(stepIndexGenerateRows);
        } else {
            setCurrentStep(stepIndexPlaceReferencePath);
        }
    }, [rainMap]);

    

    // const onVertexDrag = useCallback((evt) => {
    //     if (currentStep === stepIndexGenerateRows) {
    //         const index = evt.vertex.getIndex();
    //         const pos = evt.latlng;
    //         // const paths = pathDataRef.current.map(p => p.points);
    //         // const vertexPos = evt.vertex.latlng;
    //         // const {path, point, isCloserToStart} = findClosestPath(vertexPos, vertexPos, paths);
    //         const path = pathDataRef.current.filter(p => p.id === index)[0].points;
    //         const pointOnPath = findClosestPointOnPathLatLng(path, pos)
    //         const alt = evt.vertex.latlng.alt;
    //         // console.log(pointOnPath);
    //         evt.vertex.setLatLng(new L.LatLng(pointOnPath.lat, pointOnPath.lng, alt));
    //     }
    // }, [currentStep]);


    const onMarkerDrag = useCallback((evt) => {
        // console.log(evt.layer.options.id);
        rainMap?.onMarkerDrag(evt);
    }, [rainMap]);


    const onGeneratePathsClicked = useCallback((firstOffset, offset, smoothReference, splitAngleDeg, minDistServicePath, servicePathPull) => {
        rainMap.calculateDrivingPaths(firstOffset, offset, smoothReference, splitAngleDeg, minDistServicePath, servicePathPull);
    }, [rainMap]);

    const onExportGraphClicked = useCallback(() => {
        rainMap.exportKml();
    }, [rainMap]);

    const onFinishPathsClicked = useCallback(() => {
        setCurrentStep(stepIndexPlanRoute);
        rainMap.beginRoutePlanning(42, 9); // TODO FIXME
    }, [rainMap]);

    const onGenerateSequenceClicked = useCallback((corridorWidth, curveRadius) => {
        rainMap.beginRoutePlanning(corridorWidth, curveRadius);
    }, [rainMap]);

    const onRefreshSequenceClicked = useCallback((corridorWidth, curveRadius) => {
        rainMap.refreshSequence(corridorWidth, curveRadius);
    }, [rainMap]);

    
    useEffect(() => {
        mapRef.current?.on('editable:drag', (evt) => onMarkerDrag(evt));
    }, [onMarkerDrag]);

    return(
        <>
            <MapContainer
                center={[49.441437, 7.716687]} zoom={18} maxZoom={23} scrollWheelZoom={true}
                id={"map"} ref={mapRef} editable={true}
                editOptions={{
                    skipMiddleMarkers: true,
                }}
            >
                <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution="Tiles &copy; Esri"
                    // attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    // url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    maxNativeZoom={19}
                    maxZoom={23}
                />

                <Pane name={Const.PANE_NAME_PARCELS} style={{zIndex: 410}}/>
                <Pane name={Const.PANE_NAME_PARCEL_EDITOR_WORK} style={{ zIndex: 441 }} />
            </MapContainer>

            {/*<div className="position-absolute top-0 start-50 translate-middle-x my-0 p-2 align-self-center" style={{zIndex: Const.PANEL_OVERLAY_Z + 10, maxWidth: "70%"}}>*/}
            {/*    <UserHeader userId={userId} userName={userName} remoteAddr={remoteAddr}/>*/}
            {/*</div>*/}

            <input type="file" id="kmlInput" accept=".kml" style={{display: 'none'}} onChange={handleInputFileChange} />


            <div className="position-absolute start-0 top-50 translate-middle-y mx-1 p-2" style={{zIndex: Const.PANEL_OVERLAY_Z + 10, maxWidth: "20%"}}>
                <Card className={"shadow-lg"}>
                    <CardBody>
                        {currentStep === stepIndexSelectFeatures || currentStep === stepIndexGenerateRows
                            ? <RoverFeatureSelector paths={fieldPaths} points={fieldPoints} rainMap={rainMap}
                                                    onFeaturesSelected={onFeaturesSelected} showSaveButton={currentStep === stepIndexSelectFeatures}/>
                            : null
                        }
                        {currentStep === stepIndexPlaceWaterSource
                            ? <RoverWaterSourcePlacement rainMap={rainMap} defaultLatLng={rainMap.getWaterSourcePosLatLng()} onSave={onWaterSourcePositionDefined}/>
                            : null
                        }
                        {currentStep === stepIndexGenerateRows
                            ? <RoverPathGeneration waterSourceLatLng={rainMap.getWaterSourcePosLatLng()}
                                                   referencePath={rainMap.getReferencePathLatLng()}
                                                   onGeneratePathsClicked={onGeneratePathsClicked}
                                                   onExportClicked={onExportGraphClicked}
                                                   onFinishClicked={onFinishPathsClicked}
                            />
                            : null
                        }
                        {currentStep === stepIndexPlanRoute
                            ? <RoverRoutePlanning
                                onReturnClicked={() => {
                                    setCurrentStep(stepIndexGenerateRows);
                                    rainMap.clearLinesAndMarkers();
                                    rainMap.rebuildWaterSourceMarker();
                                    rainMap.calculateDrivingPathsFromCache();
                                }}
                                onResetClicked={onGenerateSequenceClicked}
                                onRefreshClicked={onRefreshSequenceClicked}
                                onExportKmlClicked={() => rainMap.exportKml()}
                                onExportJsonClicked={() => rainMap.exportWorkZones()}
                            />
                            : null
                        }
                    </CardBody>
                </Card>
            </div>


            <div className="position-absolute bottom-0 start-50 translate-middle-x my-2 p-2 align-self-center" style={{zIndex: Const.PANEL_OVERLAY_Z + 10, maxWidth: "80%"}}>
                <Card className={"shadow-lg"}>
                    <CardBody>
                        {
                            currentStep === stepIndexLoadKML
                            ? <>
                                <Button onClick={() => {document.getElementById('kmlInput').click()}}>Load KML</Button>
                                {selectedFileName ? `Selected: ${selectedFileName}` : 'Pick KML File'}
                                </>
                            :<></>
                        }
                    </CardBody>
                <CardFooter>
                    <RoverPathWizard step={currentStep} />
                </CardFooter>
                </Card>
            </div>


        </>
    );
}


export default RainRoverMain;